import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, ChefHat, Clock, Euro, ShoppingCart, Star, ThumbsUp, ThumbsDown, Minus, SkipForward, RefreshCw, Users, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to convert tag to translation key (e.g., "gluten-free" -> "gluten_free")
const getTagKey = (tag) => tag.replace(/-/g, '_');

function PlanDetailPage() {
  const { t } = useTranslation();
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portionFeedback, setPortionFeedback] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, index: null });
  const [ratingDialog, setRatingDialog] = useState({ open: false, recipeId: null, recipeName: '' });
  const [guestsDialog, setGuestsDialog] = useState({ open: false, index: null, currentGuests: 0 });
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [guestsCount, setGuestsCount] = useState(0);

  useEffect(() => {
    fetchPlan();
  }, [planId]);

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API}/week-plans/detail/${planId}`, { withCredentials: true });
      setPlan(response.data.plan);
    } catch (error) {
      console.error('Failed to fetch plan:', error);
      toast.error(t('common.error'));
      navigate('/plans');
    } finally {
      setLoading(false);
    }
  };

  const handlePortionFeedback = async (dishIndex, feedback) => {
    setPortionFeedback({ ...portionFeedback, [dishIndex]: feedback });
    try {
      await axios.post(`${API}/feedback/portion`, {
        plan_id: planId,
        dish_index: dishIndex,
        feedback: feedback
      }, { withCredentials: true });
      toast.success(t('portions.thanks'));
    } catch (error) {
      console.error('Failed to submit portion feedback:', error);
    }
  };

  const handleSkipDish = async () => {
    const { index } = confirmDialog;
    setConfirmDialog({ open: false, type: null, index: null });
    try {
      await axios.post(`${API}/week-plans/${planId}/skip-dish/${index}`, {}, { withCredentials: true });
      toast.success(t('plan_detail.dish_skipped'));
      setPlan(prev => {
        const updated = { ...prev };
        if (updated.dishes_per_day[index]) {
          updated.dishes_per_day[index].skipped = true;
        }
        return updated;
      });
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleReplaceDish = async () => {
    const { index } = confirmDialog;
    setConfirmDialog({ open: false, type: null, index: null });
    try {
      toast.info(t('common.loading'));
      const response = await axios.post(`${API}/week-plans/${planId}/replace-dish/${index}`, {}, { withCredentials: true });
      toast.success(t('plan_detail.dish_replaced', { dish: response.data.title || '' }));
      fetchPlan();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleUpdateGuests = async () => {
    const { index } = guestsDialog;
    try {
      await axios.patch(`${API}/week-plans/${planId}/day/${index}/guests`, {
        guests_count: parseInt(guestsCount) || 0
      }, { withCredentials: true });
      
      toast.success(t('plan_detail.guests_updated'));
      
      // Update locally
      setPlan(prev => {
        const updated = { ...prev };
        if (updated.dishes_per_day[index]) {
          updated.dishes_per_day[index].guests_count = parseInt(guestsCount) || 0;
        }
        return updated;
      });
      
      setGuestsDialog({ open: false, index: null, currentGuests: 0 });
    } catch (error) {
      console.error('Failed to update guests:', error);
      toast.error(t('common.error'));
    }
  };

  const handleSubmitRating = async () => {
    if (rating === 0) {
      toast.error(t('rating.select_stars'));
      return;
    }
    try {
      await axios.post(`${API}/recipes/${ratingDialog.recipeId}/review`, {
        rating: rating,
        comment: reviewText
      }, { withCredentials: true });
      toast.success(t('rating.thanks'));
      setRatingDialog({ open: false, recipeId: null, recipeName: '' });
      setRating(0);
      setReviewText('');
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const openGuestsDialog = (index, currentGuests) => {
    setGuestsCount(currentGuests || 0);
    setGuestsDialog({ open: true, index, currentGuests: currentGuests || 0 });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const dayNames = [
    t('days.monday'), t('days.tuesday'), t('days.wednesday'), 
    t('days.thursday'), t('days.friday'), t('days.saturday'), t('days.sunday')
  ];

  return (
    <div className="py-4 sm:py-8">
      <div className="container mx-auto px-3 sm:px-4 max-w-6xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/plans')}
          className="mb-4 sm:mb-6"
          data-testid="back-to-plans-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('plan_detail.back')}
        </Button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-[#2C3E2D]" data-testid="plan-detail-title">
              {t('plan_detail.title')}
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">{t('plan_detail.starting', { date: plan?.start_date })}</p>
          </div>
          <Button
            onClick={() => navigate(`/shopping/${planId}`)}
            className="bg-secondary hover:bg-secondary-hover text-white w-full sm:w-auto"
            data-testid="view-shopping-list-btn"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {t('plan_detail.view_shopping')}
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-8">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600 mb-1">{t('plan_detail.total_cost')}</p>
              <p className="text-2xl sm:text-3xl font-bold text-primary" data-testid="plan-total-cost">
                &euro;{plan?.total_cost_estimate_eur?.toFixed(2)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600 mb-1">{t('plan_detail.you_save')}</p>
              <p className="text-2xl sm:text-3xl font-bold text-success" data-testid="plan-savings">
                {plan?.saving_estimate_vs_country_avg_eur >= 0 ? '+' : ''}&euro;{plan?.saving_estimate_vs_country_avg_eur?.toFixed(2)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600 mb-1">{t('plan_detail.total_dishes')}</p>
              <p className="text-3xl font-bold text-secondary" data-testid="plan-dish-count">
                {plan?.dishes_per_day?.length || 0}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <h2 className="text-2xl font-heading font-bold text-[#2C3E2D]">{t('plan_detail.weekly_menu')}</h2>
          {plan?.dishes_per_day?.map((dish, index) => (
            <Card key={index} className={`overflow-hidden ${dish.skipped ? 'opacity-50' : ''}`} data-testid={`dish-card-${index}`}>
              <CardHeader className="bg-gray-50">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ChefHat className="w-5 h-5 text-primary" />
                    <span className="font-bold">{dayNames[index] || t('plan_detail.day', { num: index + 1 })}</span>
                    <span className="text-gray-600 font-normal">- {dish.recipe?.title || t('common.loading')}</span>
                    {dish.skipped && <span className="text-xs bg-gray-300 px-2 py-1 rounded">{t('plan_detail.skipped')}</span>}
                  </span>
                  
                  {/* Guests Badge - Clickable */}
                  <button
                    onClick={(e) => { e.stopPropagation(); openGuestsDialog(index, dish.guests_count); }}
                    className="flex items-center gap-1 px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-full text-sm transition-colors"
                    data-testid={`guests-badge-${index}`}
                  >
                    <Users className="w-4 h-4" />
                    <span>{dish.guests_count || 0} {t('plan_detail.guests_label')}</span>
                    <Edit2 className="w-3 h-3 ml-1" />
                  </button>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {dish.recipe && !dish.skipped ? (
                  <div className="space-y-4">
                    <p className="text-gray-700">{dish.recipe.description}</p>
                    
                    <div className="flex items-center gap-6 text-sm text-gray-600 flex-wrap">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {dish.recipe.cook_time_min} {t('common.min')}
                      </div>
                      <div className="flex items-center gap-1">
                        <Euro className="w-4 h-4" />
                        &euro;{dish.recipe.price_estimate_eur?.toFixed(2)}
                      </div>
                      <div className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs">
                        {Array.isArray(dish.recipe.cuisines) ? dish.recipe.cuisines[0] : dish.recipe.cuisines || 'General'}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmDialog({ open: true, type: 'skip', index })}
                        data-testid={`skip-dish-${index}`}
                      >
                        <SkipForward className="w-4 h-4 mr-1" />
                        {t('plan_detail.skip_dish')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmDialog({ open: true, type: 'replace', index })}
                        data-testid={`replace-dish-${index}`}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        {t('plan_detail.replace_dish')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRatingDialog({ open: true, recipeId: dish.recipe.recipe_id, recipeName: dish.recipe.title })}
                        data-testid={`rate-recipe-${index}`}
                      >
                        <Star className="w-4 h-4 mr-1" />
                        {t('rating.rate_recipe')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/recipe/${dish.recipe.recipe_id}`)}
                        data-testid={`view-recipe-${index}`}
                      >
                        {t('plan_detail.view_full_recipe')}
                      </Button>
                    </div>

                    {/* Portion Feedback */}
                    <div className="p-4 bg-amber-50 rounded-lg" data-testid={`portion-feedback-${index}`}>
                      <p className="text-sm font-medium text-amber-800 mb-2">{t('portions.feedback_question')}</p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={portionFeedback[index] === 'too_little' ? 'default' : 'outline'}
                          onClick={() => handlePortionFeedback(index, 'too_little')}
                          className={portionFeedback[index] === 'too_little' ? 'bg-red-500 hover:bg-red-600' : ''}
                          data-testid={`portion-too-little-${index}`}
                        >
                          <ThumbsDown className="w-4 h-4 mr-1" />
                          {t('portions.too_little')}
                        </Button>
                        <Button
                          size="sm"
                          variant={portionFeedback[index] === 'just_right' ? 'default' : 'outline'}
                          onClick={() => handlePortionFeedback(index, 'just_right')}
                          className={portionFeedback[index] === 'just_right' ? 'bg-green-500 hover:bg-green-600' : ''}
                          data-testid={`portion-just-right-${index}`}
                        >
                          <ThumbsUp className="w-4 h-4 mr-1" />
                          {t('portions.just_right')}
                        </Button>
                        <Button
                          size="sm"
                          variant={portionFeedback[index] === 'too_much' ? 'default' : 'outline'}
                          onClick={() => handlePortionFeedback(index, 'too_much')}
                          className={portionFeedback[index] === 'too_much' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                          data-testid={`portion-too-much-${index}`}
                        >
                          <Minus className="w-4 h-4 mr-1" />
                          {t('portions.too_much')}
                        </Button>
                      </div>
                    </div>

                    {dish.recipe.dietary_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {dish.recipe.dietary_tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="px-2 py-1 bg-success/10 text-success rounded text-xs">
                            {t(`recipes.${getTagKey(tag)}`) || tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div>
                      <h4 className="font-semibold mb-2">{t('plan_detail.ingredients')}</h4>
                      <div className="grid md:grid-cols-2 gap-2">
                        {dish.recipe.ingredients?.map((ing, ingIndex) => (
                          <div key={ingIndex} className="text-sm text-gray-700">
                            &bull; {ing.amount} {ing.unit} {ing.name}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">{t('plan_detail.cooking_steps')}</h4>
                      <ol className="list-decimal list-inside space-y-1">
                        {dish.recipe.steps?.map((step, stepIndex) => (
                          <li key={stepIndex} className="text-sm text-gray-700">{step}</li>
                        ))}
                      </ol>
                    </div>
                  </div>
                ) : dish.skipped ? (
                  <p className="text-gray-500 italic">{t('plan_detail.dish_was_skipped')}</p>
                ) : (
                  <p className="text-gray-500">{t('plan_detail.recipe_na')}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Confirm Dialog for Skip/Replace */}
        <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, type: null, index: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {confirmDialog.type === 'skip' ? t('plan_detail.confirm_skip_title') : t('plan_detail.confirm_replace_title')}
              </DialogTitle>
              <DialogDescription>
                {confirmDialog.type === 'skip' ? t('plan_detail.confirm_skip_desc') : t('plan_detail.confirm_replace_desc')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialog({ open: false, type: null, index: null })}>
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={confirmDialog.type === 'skip' ? handleSkipDish : handleReplaceDish}
                className={confirmDialog.type === 'skip' ? 'bg-red-500 hover:bg-red-600' : ''}
              >
                {t('common.confirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Guests Dialog */}
        <Dialog open={guestsDialog.open} onOpenChange={(open) => !open && setGuestsDialog({ open: false, index: null, currentGuests: 0 })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                {t('plan_detail.edit_guests_title')}
              </DialogTitle>
              <DialogDescription>
                {t('plan_detail.edit_guests_desc', { day: dayNames[guestsDialog.index] || `Dag ${(guestsDialog.index || 0) + 1}` })}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>{t('plan_detail.guests_count_label')}</Label>
              <Input
                type="number"
                min={0}
                max={20}
                value={guestsCount}
                onChange={(e) => setGuestsCount(e.target.value)}
                className="mt-2"
                data-testid="guests-count-input"
              />
              <p className="text-xs text-gray-500 mt-2">{t('plan_detail.guests_count_hint')}</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setGuestsDialog({ open: false, index: null, currentGuests: 0 })}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleUpdateGuests} data-testid="save-guests-btn">
                {t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rating Dialog */}
        <Dialog open={ratingDialog.open} onOpenChange={(open) => !open && setRatingDialog({ open: false, recipeId: null, recipeName: '' })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('rating.rate_recipe')}: {ratingDialog.recipeName}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">{t('rating.how_many_stars')}</p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className="focus:outline-none"
                      data-testid={`star-${star}`}
                    >
                      <Star
                        className={`w-8 h-8 transition-colors ${
                          star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">{t('rating.write_review')}</p>
                <Textarea
                  placeholder={t('rating.review_placeholder')}
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  rows={4}
                  data-testid="review-textarea"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRatingDialog({ open: false, recipeId: null, recipeName: '' })}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSubmitRating} data-testid="submit-rating-btn">
                {t('rating.submit')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default PlanDetailPage;
